import { FileTextOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  List,
  Spin,
  Table,
  Tabs,
  Tag,
  message,
  Row,
  Col,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Bar, Doughnut } from 'react-chartjs-2'
import { Helmet } from 'react-helmet/es/Helmet'
import { EmptyText, UrlifiedText } from '../../components/Common/CellText'
import CounterCard from '../../components/CounterCard/CounterCard'
import SpinnerInlineComponent from '../../components/SpinnerInlineComponent'
import { systemName, useSSO } from '../../config'
import {
  ACL_ACTION_TYPE,
  HHhMM,
  ISO_DATE_FORMAT,
  MODULE_CODE,
} from '../../constants'
import DashboardLayout from '../../layouts/DashboardLayout'
import { WorkRequest } from '../../requests/WorkRequest'
import { WorkScheduleRequest } from '../../requests/WorkScheduleRequest'
import utils from '../../utils'
import validator from '../../validator'
import OfficeCalendar from '../CompanyWorkScheduleListPageV2/OfficeCalendar'
import PopupNotificationPage from '../PopupNotificationPage/PopupNotificationPage'
import { LoginWrapper } from '../PortalPageV2/PortalPageV2Styled'
import {
  CardWrapper,
  DescriptionLink,
  GeneralNotif,
  RowWrapper,
} from './DashboardPageV2Styled'
import './index.css'

const { TabPane } = Tabs

const defaultTabNotification = { onlyNewsNotification: false }
const defaultSetVisible = () => { }

const WorkStatusDoughnutChart = memo(
  ({ completed = 0, inProgress = 0, overdue = 0 }) => {
    const [hoveredIndex, setHoveredIndex] = useState(null)
    const overlayRef = useRef(null)

    const total = completed + inProgress + overdue

    const centerLocatorPlugin = useMemo(
      () => ({
        id: 'centerLocator',
        afterLayout: chart => {
          if (chart.chartArea && overlayRef.current) {
            const x = (chart.chartArea.left + chart.chartArea.right) / 2
            const y = (chart.chartArea.top + chart.chartArea.bottom) / 2
            overlayRef.current.style.left = `${x}px`
            overlayRef.current.style.top = `${y}px`
          }
        },
      }),
      []
    )

    const chartData = useMemo(
      () => ({
        labels: ['Hoàn thành', 'Đang thực hiện', 'Quá hạn'],
        datasets: [
          {
            data: [completed, inProgress, overdue],
            backgroundColor: ['#00C49F', '#1a3c6e', '#e74c3c'],
            borderWidth: 2,
          },
        ],
      }),
      [completed, inProgress, overdue]
    )

    const chartOptions = useMemo(
      () => ({
        cutoutPercentage: 60,
        legend: { position: 'bottom' },
        maintainAspectRatio: false,
        tooltips: {
          enabled: false,
        },
        onHover: (event, chartElements) => {
          if (chartElements && chartElements.length > 0) {
            const idx = chartElements[0]._index
            setHoveredIndex(prev => (prev !== idx ? idx : prev))
          } else {
            setHoveredIndex(prev => (prev !== null ? null : prev))
          }
        },
      }),
      []
    )

    const getCenterContent = () => {
      if (hoveredIndex === 0) {
        return { label: 'Hoàn thành', count: completed, color: '#00C49F' }
      }
      if (hoveredIndex === 1) {
        return { label: 'Đang thực hiện', count: inProgress, color: '#1a3c6e' }
      }
      if (hoveredIndex === 2) {
        return { label: 'Quá hạn', count: overdue, color: '#e74c3c' }
      }
      return { label: 'Tổng số', count: total, color: '#1e293b' }
    }

    const { label, count, color } = getCenterContent()

    return (
      <div
        style={{ position: 'relative', width: '100%', height: 220 }}
        onMouseLeave={() => setHoveredIndex(null)}>
        <Doughnut
          data={chartData}
          options={chartOptions}
          plugins={[centerLocatorPlugin]}
        />
        <div
          ref={overlayRef}
          style={{
            position: 'absolute',
            top: '44%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: 130,
          }}>
          <span
            style={{
              fontSize: 24,
              fontWeight: 700,
              color: color,
              lineHeight: 1.1,
              transition: 'color 0.2s ease',
              display: 'block',
            }}>
            {count?.toLocaleString('vi-VN')}
          </span>
          <span
            style={{
              fontSize: 12,
              color: '#64748b',
              fontWeight: 500,
              marginTop: 4,
              display: 'block',
              whiteSpace: 'nowrap',
            }}>
            {label}
          </span>
        </div>
      </div>
    )
  }
)

const WorkByAssigneeDetailDrawer = memo(({ visible, onClose, data = [] }) => {
  const [searchText, setSearchText] = useState('')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })

  const filteredData = useMemo(() => {
    if (!searchText || !searchText.trim()) return data
    const kw = searchText.trim().toLowerCase()
    return data.filter(
      item =>
        (item.name && item.name.toLowerCase().includes(kw)) ||
        (item.username && item.username.toLowerCase().includes(kw))
    )
  }, [data, searchText])

  const columns = [
    {
      title: 'STT',
      key: 'rank',
      align: 'center',
      width: 60,
      render: (text, record, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: 'Người phụ trách',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (name, record) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>
            {name || record.username}
          </span>
          {record.username && (
            <span style={{ fontSize: 12, color: '#64748b' }}>
              @{record.username}
            </span>
          )}
        </div>
      ),
    },
    {
      title: 'Số việc',
      dataIndex: 'count',
      key: 'count',
      align: 'right',
      width: 110,
      sorter: (a, b) => (a.count || 0) - (b.count || 0),
      defaultSortOrder: 'descend',
      render: count => (
        <Tag
          color="blue"
          style={{
            fontSize: 13,
            fontWeight: 700,
            padding: '2px 10px',
            borderRadius: 12,
            margin: 0,
            background: '#eff6ff',
            color: '#1d4ed8',
            borderColor: '#bfdbfe',
          }}>
          {count?.toLocaleString('vi-VN') || 0}
        </Tag>
      ),
    },
  ]

  return (
    <Drawer
      title={
        <div style={{ fontSize: 18, fontWeight: 700, color: '#006db2' }}>
          Chi tiết Khối lượng Công việc theo Người phụ trách
        </div>
      }
      width={600}
      open={visible}
      visible={visible}
      onClose={onClose}
      bodyStyle={{ padding: '20px 24px', background: '#fafbfc' }}>
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Tìm kiếm theo họ tên hoặc tài khoản (username)..."
          allowClear
          onChange={e => {
            setSearchText(e.target.value)
            setPagination(prev => ({ ...prev, current: 1 }))
          }}
          style={{ width: '100%', maxWidth: 380 }}
        />
      </div>

      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        }}>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey={record => record.username || record.name}
          onChange={pag => {
            setPagination({ current: pag.current, pageSize: pag.pageSize })
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            className: 'custom-ant-table-pagination',
            showSizeChanger: false,
            showLessItems: true,
            style: {
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: '12px 16px',
              margin: 0,
              background: '#f8fafc',
              borderTop: '1px solid #f1f5f9',
            },
          }}
          size="middle"
        />
      </div>
    </Drawer>
  )
})

const DashboardPageV2 = props => {
  const {
    history,
    location,

    statisticStore,
    fileStore,
    authenticationStore,
    accountStore,
    commandStore,
    notificationStore,
    userStore,
    moduleStore,
    aclStore,
    tintucStore,
    infoOptionStore,
  } = props
  const { infoOptions } = infoOptionStore
  const [linkMisbi, setLinkMisbi] = useState(null)
  const optionKey = 'MISBI_HOME'

  const { currentUser, isAccountAdmin, isSuperAdmin } = authenticationStore
  const { aclActionsByUser } = aclStore

  // const { statistic } = statisticStore
  const { sideMenuCounter, dashboardSideMenuCounter } = commandStore
  const { newsList } = tintucStore

  // const [dataCongViec, setDataCongViec] = useState([{ type: '', value: 0 }])
  //

  const [isLoadingSidemenuCounter, setIsLoadingSidemenuCounter] = useState(
    false
  )
  const [isLoadingTinTuc, setIsLoadingTinTuc] = useState(false)
  //
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [, setShowDialog] = useState(false)

  const [selectedDate, setSelectedDate] = useState(
    moment().format(ISO_DATE_FORMAT)
  )
  const [isLoadingWorkSchedule, setIsLoadingWorkSchedule] = useState(false)
  const [weeklyWorkScheduleData, setWeeklyWorkScheduleData] = useState([])
  const [workScheduleListData, setWorkScheduledListData] = useState([])
  const [loadedWeekStart, setLoadedWeekStart] = useState(null)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [workStatistic, setWorkStatistic] = useState({
    completed: 0,
    in_progress: 0,
    overdue: 0,
  })
  const [workByAssignee, setWorkByAssignee] = useState([])
  const [monthlyWorkStats, setMonthlyWorkStats] = useState([])
  const [isLoadingWorkStatistic, setIsLoadingWorkStatistic] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const handleGetWorkSchedule = useCallback(async () => {
    const startOfWeek = moment(selectedDate)
      .startOf('isoWeek')
      .format(ISO_DATE_FORMAT)
    const endOfWeek = moment(selectedDate)
      .endOf('isoWeek')
      .format(ISO_DATE_FORMAT)

    if (loadedWeekStart === startOfWeek) {
      const activeDayData = weeklyWorkScheduleData.filter(
        item => moment(item.start_at).format(ISO_DATE_FORMAT) === selectedDate
      )
      setWorkScheduledListData(activeDayData)
      return
    }

    try {
      setIsLoadingWorkSchedule(true)
      const response = await WorkScheduleRequest.getWorkSchedule(
        startOfWeek,
        endOfWeek
      )

      const allData = response?.data || []
      setWeeklyWorkScheduleData(allData)
      setLoadedWeekStart(startOfWeek)

      const activeDayData = allData.filter(
        item => moment(item.start_at).format(ISO_DATE_FORMAT) === selectedDate
      )
      setWorkScheduledListData(activeDayData)
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
      setWeeklyWorkScheduleData([])
      setWorkScheduledListData([])
      setLoadedWeekStart(null)
    } finally {
      setIsLoadingWorkSchedule(false)
    }
  }, [selectedDate, loadedWeekStart, weeklyWorkScheduleData])

  useEffect(() => {
    handleGetWorkSchedule()
  }, [handleGetWorkSchedule])

  const [dateRangeFilter, setDateRangeFilter] = useState({
    from_date: `${moment().year()}-01-01`,
    to_date: `${moment().year()}-12-31`,
  })

  const handleFetchWorkStatistics = useCallback(
    async (fromDate, toDate) => {
      try {
        setIsLoadingWorkStatistic(true)
        const params = {}
        if (fromDate && toDate) {
          params.from_date = fromDate
          params.to_date = toDate
        }
        try {
          if (
            !userStore.mentionUserList ||
            userStore.mentionUserList.length === 0
          ) {
            await userStore.getMentionUserList()
          }
        } catch (e) {
          console.log('Lỗi khi lấy danh sách user:', e)
        }

        const response = await WorkRequest.getWorkStatistics(params)
        const data = response?.data || {}
        setWorkStatistic({
          completed: data.completed ?? 0,
          in_progress: data.in_progress ?? 0,
          overdue: data.over_due ?? 0,
        })

        const year = fromDate ? moment(fromDate).year() : moment().year()
        const allMonths = Array.from({ length: 12 }, (_, i) => {
          const m = String(i + 1).padStart(2, '0')
          return `${year}-${m}`
        })
        const userWorkList = Array.isArray(data.user_work) ? data.user_work : []
        const workMap = {}
        userWorkList.forEach(item => {
          workMap[item.date] = item.count_work || 0
        })

        const formattedChartData = allMonths.map(monthStr => {
          const m = monthStr.split('-')[1]
          return {
            month: `T${parseInt(m)}`,
            count: workMap[monthStr] || 0,
          }
        })
        setMonthlyWorkStats(formattedChartData)
      } catch (err) {
        console.log(err)
        message.error(err?.vi || 'Đã có lỗi xảy ra khi lấy thống kê công việc!')
      } finally {
        setIsLoadingWorkStatistic(false)
      }
    },
    [userStore]
  )

  useEffect(() => {
    handleFetchWorkStatistics(
      dateRangeFilter.from_date,
      dateRangeFilter.to_date
    )
  }, [
    handleFetchWorkStatistics,
    dateRangeFilter.from_date,
    dateRangeFilter.to_date,
  ])

  // useEffect(() => {
  //   ;(async () => {
  //     try {
  //       setIsLoadingSidemenuCounter(true)
  //       await Promise.all([
  //         commandStore.getSideMenuCounterDashboard(['WORK']),
  //         commandStore.getSideMenuCounterDashboard(['PROPOSAL']),
  //         commandStore.getSideMenuCounterDashboard(['INCOMING']),
  //         commandStore.getSideMenuCounterDashboard(['OUTGOING']),
  //         commandStore.getSideMenuCounterDashboard(['TASK']),
  //       ])
  //     } catch (err) {
  //       console.log(err)
  //       message.error(err.vi || 'Đã có lỗi xảy ra!')
  //     } finally {
  //       setIsLoadingSidemenuCounter(false)
  //     }
  //   })()
  // }, [])
  useEffect(() => {
    ; (async () => {
      const link = await infoOptionStore.load(optionKey)
      if (link && utils.validURL(link)) {
        setLinkMisbi(link)
      }
    })()
  }, [infoOptions])

  useEffect(() => {
    ; (async () => {
      try {
        setIsLoadingTinTuc(true)
        await tintucStore.getAllNews()
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
        setIsLoadingTinTuc(false)
      } finally {
        setIsLoadingTinTuc(false)
      }
    })()
  }, [])

  const isAccessControl = (moduleCode, accessControlType) => {
    if (isSuperAdmin) return true
    if (!moduleStore.checkAccessModule(moduleCode)) return false
    if (accessControlType)
      return (
        isAccountAdmin ||
        !!aclActionsByUser.find(item => item.code === accessControlType)?.status
      )
    return true
  }

  // clear workScheduleList va statistic khi ra khỏi trang dashboard
  useEffect(() => {
    return () => {
      statisticStore.clearStatistic()
      tintucStore.clear()
      commandStore.clearStore()
    }
  }, [])

  const onClickIncoming = () => {
    history.push({
      pathname: `/internal-document/incoming-document`,
      state: {
        from: location.pathname,
        onlyUnread: true,
        page: 0,
        size: 10,
      },
    })
  }
  const onClickOutgoing = () => {
    history.push({
      pathname: `/internal-document/outgoing-document`,
      state: {
        from: location.pathname,
        onlyUnread: true,
        page: 0,
        size: 10,
      },
    })
  }
  const onClickWorks = () => {
    history.push({
      pathname: `/works`,
      state: {
        from: location.pathname,
        isUnread: true,
      },
    })
  }
  const onClickMission = () => {
    history.push({
      pathname: `/mission`,
      state: {
        from: location.pathname,
      },
    })
  }
  const onClickProposal = () => {
    history.push({
      pathname: `/proposal`,
      state: {
        from: location.pathname,
        proposal_Status: 'PENDING',
      },
    })
  }

  const renderFile = file_ids => {
    if (file_ids) {
      if (file_ids.length === 0) {
        return <EmptyText>Không có tài liệu.</EmptyText>
      }
      if (file_ids.length === 1) {
        return (
          <DescriptionLink>
            <FileTextOutlined style={{ color: '#40a9ff' }} />
            <a
              onClick={e =>
                fileStore.handleDownloadFile(
                  file_ids[0].file_id,
                  file_ids[0].file_title
                )
              }>
              {file_ids[0].file_title}
            </a>
          </DescriptionLink>
        )
      }

      if (file_ids.length > 1) {
        return (
          <DescriptionLink>
            <FileTextOutlined style={{ color: '#40a9ff' }} />
            <a
              onClick={e =>
                fileStore.handleDownloadFile(
                  file_ids[0].file_id,
                  file_ids[0].file_title
                )
              }>
              {file_ids[0].file_title}
            </a>
          </DescriptionLink>
        )
      }
    }
  }

  const getShipScheduleContent = workScheduled => {
    const shipScheduleContent = []

    shipScheduleContent.push(
      <div className="work-scheduled-item__info">
        <b>Thời gian: </b>
        {workScheduled.end_at
          ? `Từ ${moment(workScheduled.start_at).format(HHhMM)} Đến ${moment(
            workScheduled.end_at
          ).format(HHhMM)}`
          : `Từ ${moment(workScheduled.start_at).format(HHhMM)}`}
      </div>
    )
    shipScheduleContent.push(
      <div className="work-scheduled-item__info">
        <b>Chủ trì: </b>
        {workScheduled.host ? (
          workScheduled.host
        ) : (
          <EmptyText>Không có chủ trì.</EmptyText>
        )}
      </div>
    )
    shipScheduleContent.push(
      <div className="work-scheduled-item__info">
        <b>Địa điểm: </b>
        <span>
          {workScheduled.location ? (
            UrlifiedText(workScheduled?.location)
          ) : (
            <EmptyText>Không có địa điểm.</EmptyText>
          )}
        </span>
      </div>
    )
    shipScheduleContent.push(
      <div className="work-scheduled-item__info">
        <b>Tài liệu: </b>
        <span>{renderFile(workScheduled?.file_ids)}</span>
      </div>
    )

    shipScheduleContent.push(
      <div className="work-scheduled-item__info">
        <b>Chuẩn bị: </b>
        <span>
          {workScheduled.preparation ? (
            workScheduled.preparation
          ) : (
            <EmptyText>Không có chuẩn bị.</EmptyText>
          )}
        </span>
      </div>
    )
    shipScheduleContent.push(
      <div className="work-scheduled-item__info">
        <b>Nội dung: </b>
        <div>
          {workScheduled.event_notice ? (
            UrlifiedText(
              workScheduled.event_notice
                .replace(/<\/?[^>]+(>|$)/g, '')
                .replace(/&nbsp;/g, '')
            )
          ) : (
            <EmptyText>Không có nội dung.</EmptyText>
          )}
        </div>
      </div>
    )

    shipScheduleContent.push(
      <div className="work-scheduled-item__info">
        <b>Thành viên tham gia: </b>
        <div>
          {workScheduled.attenders ? (
            workScheduled.attenders
          ) : (
            <EmptyText>Không có thành viên tham gia.</EmptyText>
          )}
        </div>
      </div>
    )

    return shipScheduleContent
  }

  const handleOk = async values => {
    setConfirmLoading(true)
    try {
      const response = await authenticationStore.userLogin(
        values.identifier,
        values.password
      )
      if (response.status === 200) {
        const {
          data: { name_uppercase: nameInUppercase, username },
        } = await authenticationStore.checkCurrentUser()
        console.log('[INFO]  nameInUppercase:', nameInUppercase)
        message.success(
          `Xin chào, ${utils.getNameInCapitalize(nameInUppercase)}!`
        )
        await Promise.allSettled([
          authenticationStore.getCurrentUserAvatar(),
          commandStore.getCommandList(),
          notificationStore.getUnreadNotificationCount(),
          notificationStore.getUnreadNewsCount(),
          userStore.getMentionUserList(),
          accountStore.getCurrentUserAccount(),
          moduleStore.getModuleList(),
          aclStore.getACLDetailByUser(username),
        ])
        setShowDialog(false)
      }
    } catch (err) {
      console.log('[INFO]  err:', err)
    } finally {
      setConfirmLoading(false)
    }
  }

  const loginPage = (
    <>
      <LoginWrapper
        bgImage={`${process.env.PUBLIC_URL}/assets/photos/auth-bg-2.jpg`}>
        {useSSO ? (
          <></>
        ) : (
          <Form layout={'vertical'} name={'loginForm'} onFinish={handleOk}>
            <Form.Item style={{ textAlign: 'center' }}>
              <img
                alt="login_logo"
                height={32}
                src={`${process.env.PUBLIC_URL}/assets/photos/portal_logo_white.png`}
              />
            </Form.Item>
            <Form.Item
              label="Tên đăng nhập"
              name="identifier"
              rules={[
                { required: true, message: 'Vui lòng nhập tên đăng nhập!' },
                { validator: validator.validateUsername },
              ]}>
              <Input />
            </Form.Item>
            <Form.Item
              label="Mật khẩu"
              name="password"
              rules={[{ required: true, message: 'Vui lòng nhập mật khẩu!' }]}>
              <Input.Password />
            </Form.Item>
            <Button
              type={'primary'}
              block
              htmlType={'submit'}
              loading={confirmLoading}
            // onClick={visibleLoading}
            >
              {' '}
              Đăng nhập
            </Button>

            <Button style={{ marginTop: 8 }} type={'link'} block>
              {' '}
              Quên mật khẩu?
            </Button>
            {/*<Button style={{ marginTop: 0 }} type={'link'} block onClick={showModal}> Lịch cơ quan</Button>*/}
          </Form>
        )}
      </LoginWrapper>
    </>
  )

  // const updatePassword = (
  //   <Modal
  //     title="Mật khẩu của bạn đã quá hạn!"
  //     onCancel={() => setMustChangePassword}
  //     className="ant-modal-custom"
  //     visible={mustChangePassword}>
  //     <Row align="middle">
  //       <Col className="gutter-row" span={24} offset={4}>
  //         <ChangePassword
  //           onAfterPasswordChanged={setMustChangePassword}
  //           visible={true}
  //           onCancel={setMustChangePassword}
  //         />
  //       </Col>
  //     </Row>
  //   </Modal>
  // )

  const topBlock = (
    <RowWrapper>
      <CardWrapper>
        {isAccessControl(
          MODULE_CODE.van_ban_noi_bo,
          ACL_ACTION_TYPE.menu__INTERNAL_DOCUMENT
        ) && (
            <CounterCard
              title={'Văn bản đến chưa đọc'}
              counter={dashboardSideMenuCounter?.unread_incoming ?? 0}
              icon={`${process.env.PUBLIC_URL}/assets/icons/document_outgoing_new.svg`}
              onClick={onClickIncoming}>
              <SpinnerInlineComponent
                sizeSpin={'small'}
                isLoading={isLoadingSidemenuCounter}
                backGround={'#ffffffc7'}
              />
            </CounterCard>
          )}

        {isAccessControl(
          MODULE_CODE.van_ban_noi_bo,
          ACL_ACTION_TYPE.menu__INTERNAL_DOCUMENT
        ) && (
            <CounterCard
              title={'Văn bản đi chưa đọc'}
              counter={dashboardSideMenuCounter?.unread_outgoing ?? 0}
              icon={`${process.env.PUBLIC_URL}/assets/icons/document_incoming_new.svg`}
              onClick={onClickOutgoing}>
              <SpinnerInlineComponent
                sizeSpin={'small'}
                isLoading={isLoadingSidemenuCounter}
                backGround={'#ffffffc7'}
              />
            </CounterCard>
          )}
        {isAccessControl(MODULE_CODE.cong_viec, ACL_ACTION_TYPE.menu__WORK) && (
          <CounterCard
            title={'Công việc chưa đọc'}
            counter={dashboardSideMenuCounter?.work ?? 0}
            icon={`${process.env.PUBLIC_URL}/assets/icons/work_manager_draft.svg`}
            onClick={onClickWorks}>
            <SpinnerInlineComponent
              sizeSpin={'small'}
              isLoading={isLoadingSidemenuCounter}
              backGround={'#ffffffc7'}
            />
          </CounterCard>
        )}
        {/* 
                tạm thời cmt
                {isAccessControl(
                  MODULE_CODE.nhiem_vu,
                  ACL_ACTION_TYPE.menu__MISSION
                ) && (
                  <CounterCard
                    title={'Nhiệm vụ quá hạn'}
                    counter={dashboardSideMenuCounter?.overdue_task ?? 0}
                    icon={`${process.env.PUBLIC_URL}/assets/icons/work_manager_overdue.svg`}
                    onClick={onClickMission}>
                    <SpinnerInlineComponent
                      sizeSpin={'small'}
                      isLoading={isLoadingSidemenuCounter}
                      backGround={'#ffffffc7'}
                    />
                  </CounterCard>
                )} */}
        {isAccessControl(
          MODULE_CODE.hanh_chinh,
          ACL_ACTION_TYPE.menu__PROPOSAL
        ) && (
            <CounterCard
              title={'Đơn hành chính chờ duyệt'}
              counter={dashboardSideMenuCounter?.proposal_other ?? 0}
              icon={`${process.env.PUBLIC_URL}/assets/icons/work_manager_processing.svg`}
              onClick={onClickProposal}>
              <SpinnerInlineComponent
                sizeSpin={'small'}
                isLoading={isLoadingSidemenuCounter}
                backGround={'#ffffffc7'}
              />
            </CounterCard>
          )}
      </CardWrapper>
      {isAccessControl(
        MODULE_CODE.thong_bao_chung,
        ACL_ACTION_TYPE.menu__NEWS
      ) && (
          <GeneralNotif
            header={<div>Thông báo chung </div>}
            footer={false}
            bordered
            dataSource={newsList.slice(0, 4)}
            renderItem={item => (
              <List.Item
                key={item.id}
                onClick={() => {
                  history.push(`/utility/general-notifications/view/${item.id}`)
                }}>
                {item.subject}
              </List.Item>
            )}>
            <SpinnerInlineComponent
              sizeSpin={'small'}
              isLoading={isLoadingTinTuc}
              backGround={'#ffffffc7'}
              tip={'Đang tải thông báo chung...'}
            />
          </GeneralNotif>
        )}
    </RowWrapper>
  )

  const iFrame = (
    <iframe
      title="TONG QUAN "
      width="100%"
      height="300"
      src={linkMisbi}
      frameBorder={0}
      allowFullScreen={true}></iframe>
  )

  return (
    <div>
      {currentUser != null ? (
        <DashboardLayout
          title={'Link People - Link the World'}
          backgroundColor={'#f2f3f8'}>
          <Helmet>
            <title>Dashboard | {systemName} Portal</title>
          </Helmet>
          <div style={{ position: 'relative' }}>
            {/* {systemName == 'VIMC' ? iFrame : topBlock} */}
            {/* {systemName == 'VIMC' ? iFrame : topBlock} */}
            {/* {topBlock} */}
            <Row
              gutter={[16, 16]}
              style={{
                marginBottom: 24,
                marginTop: 16,
                alignItems: 'stretch',
              }}>
              {/* Phần thống kê (Bên trái) */}
              <Col
                xs={24} lg={16}
                style={{
                  display: 'flex',
                }}>
                <div
                  style={{
                    flex: 1,
                    background: '#fff',
                    borderRadius: 8,
                    padding: 16,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                  <Spin spinning={isLoadingWorkStatistic}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 12,
                          borderBottom: '1px solid #f0f2f5',
                          paddingBottom: 10,
                        }}>
                        <p
                          style={{
                            fontWeight: 700,
                            fontSize: 16,
                            margin: 0,
                            color: '#006db2',
                          }}>
                          Thống kê công việc
                        </p>
                        <DatePicker
                          picker="year"
                          size="small"
                          placeholder="Chọn năm"
                          defaultValue={moment()}
                          onChange={date => {
                            if (date) {
                              const year = date.year()
                              setDateRangeFilter({
                                from_date: `${year}-01-01`,
                                to_date: `${year}-12-31`,
                              })
                            } else {
                              setDateRangeFilter({
                                from_date: undefined,
                                to_date: undefined,
                              })
                            }
                          }}
                        />
                      </div>

                      <Row
                        gutter={[16, 16]}
                        style={{
                          flex: 1,
                          paddingTop: 4,
                        }}>
                        {/* Donut */}
                        <Col
                          xs={24} xl={12}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            borderRight: '1px solid #f0f2f5',
                            paddingRight: 12,
                            marginBottom: 16,
                          }}>
                          <p
                            style={{
                              fontWeight: 600,
                              fontSize: 15,
                              textAlign: 'center',
                              marginBottom: 12,
                            }}>
                            Thống kê công việc của tôi
                          </p>
                          <WorkStatusDoughnutChart
                            completed={workStatistic.completed}
                            inProgress={workStatistic.in_progress}
                            overdue={workStatistic.overdue}
                          />
                        </Col>

                        {/* Bar + nút xem chi tiết */}
                        <Col
                          xs={24} xl={12}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            paddingLeft: 4,
                          }}>
                          <div>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 12,
                              }}>
                              <p
                                style={{
                                  fontWeight: 600,
                                  fontSize: 15,
                                  margin: 0,
                                }}>
                                Khối lượng xử lý theo thời gian
                              </p>
                            </div>
                            <div style={{ position: 'relative', height: 220, width: '100%' }}>
                              <Bar
                                data={{
                                  labels: monthlyWorkStats.map(i => i.month),
                                  datasets: [
                                    {
                                      data: monthlyWorkStats.map(i => i.count),
                                      backgroundColor: '#1a3c6e',
                                    },
                                  ],
                                }}
                                options={{
                                  legend: { display: false },
                                  scales: {
                                    xAxes: [
                                      {
                                        ticks: {
                                          maxRotation: 0,
                                          minRotation: 0,
                                        },
                                      },
                                    ],
                                    yAxes: [
                                      {
                                        ticks: { beginAtZero: true, precision: 0 },
                                      },
                                    ],
                                  },
                                  maintainAspectRatio: false,
                                }}
                              />
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <WorkByAssigneeDetailDrawer
                      visible={isDrawerOpen}
                      onClose={() => setIsDrawerOpen(false)}
                      data={workByAssignee}
                    />
                  </Spin>
                </div>
              </Col>

              {/* Phần thông báo của tôi (Bên phải - Tái sử dụng PopupNotificationPage) */}
              <Col
                xs={24} lg={8}
                style={{
                  display: 'flex',
                }}>
                <div
                  style={{
                    flex: 1,
                    background: '#fff',
                    borderRadius: 8,
                    padding: '16px 16px 0 16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      overflow: 'hidden',
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                        borderBottom: '1px solid #f0f2f5',
                        paddingBottom: 10,
                      }}>
                      <p
                        style={{
                          fontWeight: 700,
                          fontSize: 16,
                          margin: 0,
                          color: '#006db2',
                        }}>
                        Thông báo của tôi
                      </p>
                      <Button
                        type="link"
                        size="small"
                        onClick={() =>
                          history.push(
                            '/notification?only_news_notification=false'
                          )
                        }>
                        Xem tất cả
                      </Button>
                    </div>
                    <div
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        paddingRight: 4,
                        maxHeight: 290,
                      }}>
                      <PopupNotificationPage
                        limit={10}
                        changedTabsNotification={defaultTabNotification}
                        onlyNewsNotification={false}
                        setVisibleNotification={defaultSetVisible}
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            {isAccessControl(
              MODULE_CODE.lich_co_quan,
              ACL_ACTION_TYPE.menu__WORK_SCHEDULE
            ) && (
                <>
                  <Spin spinning={isLoadingWorkSchedule}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 16,
                      }}>
                      <p style={{ fontWeight: 600, fontSize: 20, margin: 0 }}>
                        Lịch ngày
                      </p>
                      <DatePicker
                        allowClear={false}
                        picker="week"
                        onChange={date =>
                          setSelectedDate(date?.format(ISO_DATE_FORMAT))
                        }
                        placeholder={'Chọn tuần'}
                        format={date => `Tuần ${date.isoWeek()}, ${date.year()}`}
                        value={moment(selectedDate)}
                        style={{
                          height: 32,
                          width: 175,
                          margin: '0 10px 0 10px',
                        }}
                      />
                    </div>
                    <OfficeCalendar
                      selectedDate={selectedDate}
                      data={workScheduleListData}
                      selectedEventId={selectedEvent?.id}
                      onEventClick={setSelectedEvent}
                      isShowPopoverContent
                      onChangeDate={date =>
                        setSelectedDate(date?.format(ISO_DATE_FORMAT))
                      }
                    />
                  </Spin>
                </>
              )}
            {/* {updatePassword} */}
          </div>
        </DashboardLayout>
      ) : (
        loginPage
      )}
    </div>
  )
}

export default memo(
  inject(
    'loadingAnimationStore',
    'commonStore',
    'statisticStore',
    'fileStore',
    'authenticationStore',
    'accountStore',
    'userStore',
    'companyStore',
    'notificationStore',
    'commandStore',
    'taskManagementStore',
    'taskManagementCommentStore',
    'moduleStore',
    'tintucStore',
    'aclStore',
    'infoOptionStore'
  )(observer(DashboardPageV2))
)
